// /src/services/api.ts
import { API_BASE_URL } from "../config";
import { getToken } from "../lib/tokenStorage";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

async function baseRequest<T>(
  endpoint: string,
  method: HttpMethod,
  body?: unknown,
  init?: RequestInit
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const token = getToken();
  const headers: Record<string, string> = {
    ...(body ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(init?.headers as Record<string, string> | undefined),
  };

  console.log(`[API] ${method} ${endpoint}`, {
    hasToken: !!token,
    headers: Object.keys(headers),
  });

  const res = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    ...init,
  });

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const data = isJson ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) {
    const message = (isJson && (data as any)?.message) || `HTTP ${res.status}`;
    throw new Error(message);
  }

  return (data as T) ?? ({} as T);
}

export const api = {
  get: <T>(endpoint: string, init?: RequestInit) =>
    baseRequest<T>(endpoint, "GET", undefined, init),
  post: <T>(endpoint: string, body?: unknown, init?: RequestInit) =>
    baseRequest<T>(endpoint, "POST", body, init),
  put: <T>(endpoint: string, body?: unknown, init?: RequestInit) =>
    baseRequest<T>(endpoint, "PUT", body, init),
  patch: <T>(endpoint: string, body?: unknown, init?: RequestInit) =>
    baseRequest<T>(endpoint, "PATCH", body, init),
  delete: <T>(endpoint: string, init?: RequestInit) =>
    baseRequest<T>(endpoint, "DELETE", undefined, init),
};

export const blogPosts = {
  list: (filters?: any) => {
    const params = new URLSearchParams();
    if (filters?.page) params.append("page", filters.page);
    if (filters?.limit) params.append("limit", filters.limit);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.status) params.append("status", filters.status);
    if (filters?.category) params.append("category", filters.category);
    if (filters?.author) params.append("author", filters.author);
    if (filters?.featured) params.append("featured", filters.featured);
    if (filters?.sort_by) params.append("sort_by", filters.sort_by);
    if (filters?.sort_order) params.append("sort_order", filters.sort_order);

    const queryString = params.toString();
    const endpoint = queryString
      ? `/blog-posts.php?action=list&${queryString}`
      : "/blog-posts.php?action=list";
    return api.get(endpoint);
  },
  get: (id: number | string) => api.get(`/blog-posts.php?action=get&id=${id}`),
  create: (data: any) => api.post("/blog-posts.php", data),
  update: (id: number | string, data: any) =>
    api.put(`/blog-posts.php?id=${id}`, data),
  delete: (id: number | string) => api.delete(`/blog-posts.php?id=${id}`),
  getCategories: () => api.get("/blog-posts.php?action=categories"),
  getTags: (limit?: number) => {
    const endpoint = limit
      ? `/blog-posts.php?action=tags&limit=${limit}`
      : "/blog-posts.php?action=tags";
    return api.get(endpoint);
  },
  getAuthors: () => api.get("/blog-posts.php?action=authors"),
  getStats: () => api.get("/blog-posts.php?action=stats"),
};

export const siteSettings = {
  /**
   * Get a single setting by key
   * @param key The setting key
   * @returns The setting value
   */
  get: (key: string) => api.get(`/site-settings.php?action=get&key=${key}`),

  /**
   * Get all settings, optionally filtered by group
   * @param group Optional group name to filter settings
   * @returns Object with all settings where key is the setting_key
   */
  getAll: (group?: string) => {
    const endpoint = group
      ? `/site-settings.php?group=${group}`
      : "/site-settings.php";
    return api.get(endpoint);
  },

  /**
   * Update a setting
   * @param key The setting key
   * @param value The new value
   * @returns Success response
   */
  update: (key: string, value: any) =>
    api.post("/site-settings.php?action=update", { key, value }),
};
